import { useState, useEffect } from 'react';
import { UserPlus, Shield, Check, X, RefreshCw, Key, Trash2, KeyRound, MapPin, History } from 'lucide-react';
import { localApi } from '../utils/localApi';

interface AppUser {
  id: string;
  email: string;
  role: string;
  zones?: string;
  created_at: string;
  last_sign_in_at: string | null;
}

export default function AdminUserManagement() {
  const [users, setUsers] = useState<AppUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // Form state
  const [isCreating, setIsCreating] = useState(false);
  const [newEmail, setNewEmail] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [newRole, setNewRole] = useState<'driver' | 'conductor' | 'station_master' | 'operator_traction' | 'admin'>('driver');
  const [actionMessage, setActionMessage] = useState<{type: 'success' | 'error', text: string} | null>(null);

  // Reset Password state
  const [resettingUser, setResettingUser] = useState<AppUser | null>(null);
  const [resetPasswordValue, setResetPasswordValue] = useState('');
  const [isResetting, setIsResetting] = useState(false);

  // Zones state
  const [editingZonesUser, setEditingZonesUser] = useState<AppUser | null>(null);
  const [userZones, setUserZones] = useState<string[]>([]);
  const [isUpdatingZones, setIsUpdatingZones] = useState(false);

  // Blackbox User Activity state
  const [viewingActivityUser, setViewingActivityUser] = useState<AppUser | null>(null);
  const [userEvents, setUserEvents] = useState<any[]>([]);
  const [loadingEvents, setLoadingEvents] = useState(false);

  useEffect(() => {
    if (viewingActivityUser) {
      loadUserEvents(viewingActivityUser.id);
    } else {
      setUserEvents([]);
    }
  }, [viewingActivityUser]);

  const loadUserEvents = async (userId: string) => {
    setLoadingEvents(true);
    try {
      const allEvents = await localApi.getBlackboxEvents();
      const filtered = allEvents.filter((ev: any) => ev.driver_id === userId);
      setUserEvents(filtered);
    } catch (err) {
      console.error("Erreur de chargement des évènements boîte noire:", err);
    }
    setLoadingEvents(false);
  };

  const fetchUsers = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await localApi.getUsers();
      setUsers(data as AppUser[]);
    } catch (err: any) {
      setError(err.message || 'Erreur inattendue');
      setUsers([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  const handleCreateUser = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsCreating(true);
    setActionMessage(null);

    try {
      if (newPassword.length < 6) {
        throw new Error("Le mot de passe doit faire au moins 6 caractères.");
      }

      await localApi.createUser({
        email: newEmail,
        password: newPassword,
        role: newRole
      });

      setActionMessage({ type: 'success', text: `L'utilisateur ${newEmail} a été créé avec le rôle ${newRole}. Vous pouvez vous connecter immédiatement avec ce mot de passe.` });
      setNewEmail('');
      setNewPassword('');
      setNewRole('driver'); // Reset form
      fetchUsers(); // Refresh list
    } catch (err: any) {
      console.error(err);
      setActionMessage({ type: 'error', text: err.message || "Erreur lors de la création de l'utilisateur." });
    } finally {
      setIsCreating(false);
    }
  };

  const handleDeleteUser = async (userId: string, email: string) => {
    // Prevent admin from deleting themselves accidentally (safety check)
    if (email === 'admin@nexus.cfc' || email === 'admin@nexus.rail') {
      alert("Erreur de sécurité : Impossible de supprimer le compte super-administrateur principal.");
      return;
    }

    if (!window.confirm(`Êtes-vous sûr de vouloir supprimer définitivement le compte : ${email} ?\nToute son activité sera perdue.`)) {
      return;
    }

    try {
      setLoading(true);
      await localApi.deleteUser(userId);
      setActionMessage({ type: 'success', text: `L'utilisateur ${email} a été supprimé definitivement.` });
      fetchUsers(); // Refresh list automatically
    } catch (err: any) {
      console.error(err);
      alert(`Erreur lors de la suppression : ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!resettingUser) return;
    
    setIsResetting(true);
    try {
      if (resetPasswordValue.length < 6) {
        throw new Error("Le mot de passe doit faire au moins 6 caractères.");
      }

      await localApi.updateUserPassword(resettingUser.id, resetPasswordValue);

      setActionMessage({ type: 'success', text: `Le mot de passe de ${resettingUser.email} a été réinitialisé avec succès.` });
      setResettingUser(null);
      setResetPasswordValue('');
    } catch (err: any) {
      console.error(err);
      alert(`Erreur: ${err.message}`);
    } finally {
      setIsResetting(false);
    }
  };

  const handleOpenZonesModal = (user: AppUser) => {
    setEditingZonesUser(user);
    const zonesStr = user.zones || '';
    const zonesList = zonesStr ? zonesStr.split(',').map(z => z.trim()) : [];
    setUserZones(zonesList);
  };

  const handleToggleZone = (zone: string) => {
    setUserZones(prev => 
      prev.includes(zone) ? prev.filter(z => z !== zone) : [...prev, zone]
    );
  };

  const handleSaveZones = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingZonesUser) return;
    setIsUpdatingZones(true);
    try {
      const zonesString = userZones.join(',');
      await localApi.updateUserZones(editingZonesUser.id, zonesString);
      setActionMessage({ type: 'success', text: `Les zones d'accès de ${editingZonesUser.email} ont été mises à jour avec succès (${zonesString || 'aucune'}).` });
      setEditingZonesUser(null);
      fetchUsers();
    } catch (err: any) {
      console.error(err);
      alert(`Erreur: ${err.message}`);
    } finally {
      setIsUpdatingZones(false);
    }
  };

  return (
    <div className="flex flex-col h-full bg-[#0a0f1c] text-slate-300">
      
      {/* HEADER */}
      <div className="flex items-center justify-between p-6 border-b border-white/5 bg-slate-900/50">
        <div>
          <h2 className="text-xl font-black text-white uppercase tracking-widest flex items-center gap-2">
            <Shield className="text-red-500" /> Gestion des Accès
          </h2>
          <p className="text-xs text-slate-400 mt-1">Création et attribution des rôles sécurisées.</p>
        </div>
        <button 
          onClick={fetchUsers} 
          disabled={loading}
          className="flex items-center gap-2 px-4 py-2 bg-slate-800 hover:bg-slate-700 rounded-xl transition-colors border border-white/5"
        >
          <RefreshCw size={16} className={loading ? "animate-spin text-cyan-500" : "text-slate-400"} />
          <span className="text-xs font-bold uppercase tracking-widest">Actualiser</span>
        </button>
      </div>

      <div className="flex-1 overflow-y-auto p-6 flex flex-col lg:flex-row gap-6">
        
        {/* LEFT COLUMN: LIST OF USERS */}
        <div className="flex-[2] flex flex-col gap-4">
          <h3 className="text-sm font-black uppercase tracking-widest text-slate-500 mb-2">Comptes Actifs</h3>
          
          {error ? (
            <div className="bg-red-500/10 border border-red-500/30 p-6 rounded-2xl flex flex-col items-center justify-center text-center gap-4">
               <Shield size={32} className="text-red-400" />
               <div>
                  <h4 className="text-red-400 font-bold mb-2">Erreur d'Accès</h4>
                  <p className="text-sm text-red-200/70 max-w-md">{error}</p>
               </div>
            </div>
          ) : loading ? (
            <div className="flex-1 flex items-center justify-center min-h-[200px]">
              <div className="w-8 h-8 border-4 border-cyan-500/30 border-t-cyan-500 rounded-full animate-spin"></div>
            </div>
          ) : users.length === 0 ? (
            <div className="bg-slate-800/20 border border-white/5 p-6 rounded-2xl text-center text-slate-500">
              Aucun utilisateur trouvé ou accès refusé.
            </div>
          ) : (
            <div className="space-y-2">
              {users.map((u) => (
                <div key={u.id} className="bg-slate-800/40 border border-white/5 p-4 rounded-xl flex flex-col sm:flex-row sm:items-center justify-between gap-4 hover:bg-slate-800/60 transition-colors group">
                  <div className="min-w-0 flex-1">
                    <p className="text-white font-bold truncate" title={u.email}>{u.email}</p>
                    <p className="text-xs text-slate-500 mt-0.5">ID: {u.id.split('-')[0]}... • Zones: <span className="text-cyan-400 font-semibold">{u.zones || 'Aucune'}</span></p>
                  </div>
                  <div className="flex flex-wrap items-center gap-3 justify-between sm:justify-end">
                     <span className={`px-2.5 py-1 text-[10px] font-black uppercase tracking-widest rounded-md border shrink-0
                        ${u.role === 'admin' ? 'bg-red-500/10 text-red-400 border-red-500/20' : 
                          u.role === 'operator_traction' ? 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20' :
                          u.role === 'station_master' ? 'bg-purple-500/10 text-purple-400 border-purple-500/20' :
                          u.role === 'conductor' ? 'bg-amber-500/10 text-amber-400 border-amber-500/20' :
                          'bg-cyan-500/10 text-cyan-400 border-cyan-500/20'}`
                     }>
                       {u.role === 'operator_traction' ? 'Opérateur Traction' : (u.role || 'driver')}
                     </span>
                     <div className="text-right shrink-0">
                       <p className="text-[10px] text-slate-500 uppercase">Créé le</p>
                       <p className="text-xs font-mono">{new Date(u.created_at).toLocaleDateString()}</p>
                     </div>
                     <div className="flex bg-slate-900 border border-white/5 rounded-xl ml-2 overflow-hidden sm:opacity-0 group-hover:opacity-100 transition-opacity">
                       <button 
                         onClick={() => handleOpenZonesModal(u)}
                         className="w-8 h-8 flex items-center justify-center text-slate-400 hover:bg-slate-700 hover:text-white transition-colors border-r border-white/5"
                         title={`Gérer les zones de ${u.email}`}
                       >
                          <MapPin size={14} />
                       </button>
                       <button 
                         onClick={() => setViewingActivityUser(u)}
                         className="w-8 h-8 flex items-center justify-center text-slate-400 hover:bg-slate-700 hover:text-white transition-colors border-r border-white/5"
                         title={`Boîte noire / Activité de ${u.email}`}
                       >
                          <History size={14} />
                       </button>
                       <button 
                         onClick={() => setResettingUser(u)}
                         className="w-8 h-8 flex items-center justify-center text-slate-400 hover:bg-slate-700 hover:text-white transition-colors"
                         title={`Modifier mot de passe ${u.email}`}
                       >
                          <KeyRound size={14} />
                       </button>
                       <div className="w-[1px] bg-white/5"></div>
                       <button 
                         onClick={() => handleDeleteUser(u.id, u.email)}
                         className="w-8 h-8 flex items-center justify-center text-red-500/70 hover:bg-red-500 hover:text-white transition-colors"
                         title={`Supprimer ${u.email}`}
                       >
                          <Trash2 size={14} />
                       </button>
                     </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* RIGHT COLUMN: CREATE USER FORM */}
        <div className="flex-[1] min-w-[300px]">
          <div className="bg-slate-800/50 border border-cyan-500/20 rounded-2xl p-6 shadow-xl sticky top-6">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 rounded-full bg-cyan-500/10 flex items-center justify-center border border-cyan-500/20">
                <UserPlus size={20} className="text-cyan-400" />
              </div>
              <h3 className="text-white font-black uppercase tracking-widest text-sm">Nouvel Agent</h3>
            </div>

            {actionMessage && (
              <div className={`p-4 rounded-xl mb-6 text-sm flex items-start gap-3 border ${
                actionMessage.type === 'success' 
                  ? 'bg-green-500/10 text-green-400 border-green-500/30' 
                  : 'bg-red-500/10 text-red-400 border-red-500/30'
              }`}>
                {actionMessage.type === 'success' ? <Check size={18} className="shrink-0 mt-0.5" /> : <X size={18} className="shrink-0 mt-0.5" />}
                <p>{actionMessage.text}</p>
              </div>
            )}

            <form onSubmit={handleCreateUser} className="space-y-5">
              <div className="space-y-1.5">
                <label className="text-[10px] font-black text-cyan-500 uppercase tracking-widest ml-1">Adresse E-mail</label>
                <input 
                  type="email" 
                  required
                  value={newEmail}
                  onChange={e => setNewEmail(e.target.value)}
                  className="w-full bg-black/40 border border-white/10 rounded-xl p-3.5 text-white placeholder:text-slate-600 focus:outline-none focus:border-cyan-500/50 transition-colors"
                  placeholder="nom.prenom@nexus.cfc"
                />
              </div>

              <div className="space-y-1.5">
                <label className="text-[10px] font-black text-cyan-500 uppercase tracking-widest ml-1 flex justify-between">
                  <span>Mot de Passe Provisoire</span>
                  <span className="text-slate-500 lowercase font-normal">(min. 6 char)</span>
                </label>
                <div className="relative">
                  <input 
                    type="password" 
                    required
                    minLength={6}
                    value={newPassword}
                    onChange={e => setNewPassword(e.target.value)}
                    className="w-full bg-black/40 border border-white/10 rounded-xl p-3.5 pl-10 text-white placeholder:text-slate-600 focus:outline-none focus:border-cyan-500/50 transition-colors font-mono"
                    placeholder="••••••••"
                  />
                  <Key size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-500" />
                </div>
              </div>

              <div className="space-y-1.5">
                <label className="text-[10px] font-black text-cyan-500 uppercase tracking-widest ml-1">Rôle Opérationnel</label>
                <select 
                  value={newRole}
                  onChange={e => setNewRole(e.target.value as any)}
                  className="w-full bg-black/40 border border-white/10 rounded-xl p-3.5 text-white focus:outline-none focus:border-cyan-500/50 transition-colors cursor-pointer"
                >
                  <option value="driver">Conducteur (Standard)</option>
                  <option value="conductor">Chef de Train</option>
                  <option value="station_master">Chef de Gare</option>
                  <option value="operator_traction">Opérateur Traction</option>
                  <option value="admin">Administrateur (PCC)</option>
                </select>
              </div>

              <button 
                type="submit" 
                disabled={isCreating}
                className="w-full bg-cyan-600 hover:bg-cyan-500 disabled:opacity-50 disabled:cursor-not-allowed text-white font-black uppercase tracking-widest py-4 rounded-xl shadow-lg shadow-cyan-900/50 transition-all active:scale-95 mt-4 flex items-center justify-center gap-2"
              >
                {isCreating ? (
                  <><div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div> Création...</>
                ) : (
                  <>Créer le Compte</>
                )}
              </button>
            </form>
          </div>
        </div>
      </div>

      {/* MODAL: RESET PASSWORD */}
      {resettingUser && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-slate-900 border border-white/10 p-6 rounded-2xl w-full max-w-sm relative">
            <button 
              onClick={() => { setResettingUser(null); setResetPasswordValue(''); }}
              className="absolute top-4 right-4 text-slate-500 hover:text-white transition-colors"
            >
              <X size={20} />
            </button>
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-full bg-cyan-500/10 flex items-center justify-center border border-cyan-500/20">
                <KeyRound size={20} className="text-cyan-400" />
              </div>
              <div>
                <h3 className="text-white font-black uppercase tracking-widest text-sm">Forcer Mot de passe</h3>
                <p className="text-xs text-slate-400">{resettingUser.email}</p>
              </div>
            </div>

            <form onSubmit={handleResetPassword}>
               <div className="space-y-1.5 mb-6">
                  <label className="text-[10px] font-black text-white uppercase tracking-widest flex justify-between">
                    <span>Nouveau Mot de Passe</span>
                    <span className="text-slate-500 lowercase font-normal">(min. 6 char)</span>
                  </label>
                  <div className="relative">
                    <input 
                      type="password" 
                      required
                      minLength={6}
                      value={resetPasswordValue}
                      onChange={e => setResetPasswordValue(e.target.value)}
                      className="w-full bg-black border border-white/10 rounded-xl p-3.5 pl-10 text-white focus:outline-none focus:border-cyan-500/50 transition-colors font-mono"
                      placeholder="••••••••"
                      autoFocus
                    />
                    <Key size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-500" />
                  </div>
                </div>
                <div className="flex gap-3">
                  <button type="button" onClick={() => { setResettingUser(null); setResetPasswordValue(''); }} className="flex-1 py-3 bg-slate-800 text-white text-xs font-bold rounded-xl hover:bg-slate-700 transition-colors uppercase tracking-widest">
                    Annuler
                  </button>
                  <button type="submit" disabled={isResetting} className="flex-1 py-3 bg-cyan-600 hover:bg-cyan-500 disabled:opacity-50 text-white text-xs font-bold rounded-xl transition-colors uppercase tracking-widest flex items-center justify-center gap-2 shadow-lg shadow-cyan-900/40">
                    {isResetting ? 'Patientez...' : 'Confirmer'}
                  </button>
                </div>
            </form>
          </div>
        </div>
      )}

      {/* MODAL: MANAGE ZONES */}
      {editingZonesUser && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-slate-900 border border-white/10 p-6 rounded-2xl w-full max-w-sm relative">
            <button 
              onClick={() => { setEditingZonesUser(null); }}
              className="absolute top-4 right-4 text-slate-500 hover:text-white transition-colors"
            >
              <X size={20} />
            </button>
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 rounded-full bg-cyan-500/10 flex items-center justify-center border border-cyan-500/20">
                <MapPin size={20} className="text-cyan-400" />
              </div>
              <div>
                <h3 className="text-white font-black uppercase tracking-widest text-sm">Zones d'Accès</h3>
                <p className="text-xs text-slate-400">{editingZonesUser.email}</p>
              </div>
            </div>

            <form onSubmit={handleSaveZones} className="space-y-6">
              <div className="space-y-3">
                <p className="text-[10px] font-black text-cyan-500 uppercase tracking-widest ml-1">Sélectionner les zones autorisées :</p>
                
                {['Bastia', 'Ajaccio', 'Calvi'].map(zone => {
                  const isChecked = userZones.includes(zone);
                  return (
                    <button
                      key={zone}
                      type="button"
                      onClick={() => handleToggleZone(zone)}
                      className={`w-full flex items-center justify-between p-4 rounded-xl border transition-all active:scale-[0.98] ${
                        isChecked 
                          ? 'bg-cyan-500/10 border-cyan-500 text-white' 
                          : 'bg-black/40 border-white/10 text-slate-400 hover:border-white/20'
                      }`}
                    >
                      <span className="font-bold text-sm">{zone}</span>
                      <div className={`w-5 h-5 rounded-full border flex items-center justify-center transition-colors ${
                        isChecked ? 'bg-cyan-500 border-cyan-500 text-slate-900' : 'border-white/20'
                      }`}>
                        {isChecked && <Check size={12} strokeWidth={3} />}
                      </div>
                    </button>
                  );
                })}
              </div>

              <div className="flex gap-3">
                <button type="button" onClick={() => setEditingZonesUser(null)} className="flex-1 py-3 bg-slate-800 text-white text-xs font-bold rounded-xl hover:bg-slate-700 transition-colors uppercase tracking-widest">
                  Annuler
                </button>
                <button type="submit" disabled={isUpdatingZones} className="flex-1 py-3 bg-cyan-600 hover:bg-cyan-500 disabled:opacity-50 text-white text-xs font-bold rounded-xl transition-colors uppercase tracking-widest flex items-center justify-center gap-2 shadow-lg shadow-cyan-900/40">
                  {isUpdatingZones ? 'Sauvegarde...' : 'Enregistrer'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
      {/* MODAL: VIEW USER ACTIVITY (BLACK BOX) */}
      {viewingActivityUser && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-slate-900 border border-white/10 p-6 rounded-2xl w-full max-w-3xl relative shadow-2xl">
            <button 
              onClick={() => setViewingActivityUser(null)}
              className="absolute top-4 right-4 text-slate-500 hover:text-white transition-colors"
            >
              <X size={20} />
            </button>
            
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 rounded-full bg-red-500/10 flex items-center justify-center border border-red-500/20">
                <History size={20} className="text-red-400" />
              </div>
              <div>
                <h3 className="text-white font-black uppercase tracking-widest text-sm">Boîte Noire / Activité Agent</h3>
                <p className="text-xs text-slate-400">{viewingActivityUser.email}</p>
              </div>
            </div>

            <div className="bg-black/40 border border-white/5 rounded-2xl p-4 min-h-[300px] max-h-[60vh] overflow-y-auto custom-scrollbar space-y-3">
              {loadingEvents ? (
                <div className="flex items-center justify-center min-h-[250px]">
                  <div className="w-8 h-8 border-4 border-cyan-500/30 border-t-cyan-500 rounded-full animate-spin"></div>
                </div>
              ) : userEvents.length === 0 ? (
                <div className="flex flex-col items-center justify-center text-center py-20 text-slate-600 space-y-2">
                  <Shield size={32} className="opacity-30" />
                  <p className="text-xs uppercase font-black tracking-widest">Aucun évènement enregistré</p>
                  <p className="text-[10px] font-medium text-slate-700">Toutes les actions de sécurité de cet agent apparaîtront ici.</p>
                </div>
              ) : (
                userEvents.map((ev) => {
                  let badgeColor = "bg-slate-500/10 text-slate-400 border border-slate-500/20";
                  let eventTitle = ev.event_type;
                  let details = "";

                  const payload = typeof ev.payload === 'string' ? JSON.parse(ev.payload) : ev.payload;

                  if (ev.event_type === 'MISSION_START') {
                    badgeColor = "bg-green-500/10 text-green-400 border border-green-500/20";
                    eventTitle = "Début Mission";
                    details = `Mission TR ${payload?.trainNumber || ev.train_number} - ${payload?.lineName || 'Ligne'}`;
                  } else if (ev.event_type === 'MISSION_END') {
                    badgeColor = "bg-emerald-500/10 text-emerald-400 border border-emerald-500/20";
                    eventTitle = "Fin Mission";
                    details = `Mission TR ${payload?.trainNumber || ev.train_number} - ${payload?.lineName || 'Ligne'}`;
                  } else if (ev.event_type === 'MISSION_CANCEL') {
                    badgeColor = "bg-amber-500/10 text-amber-400 border border-amber-500/20";
                    eventTitle = "Annulation Mission";
                    details = `Mission TR ${payload?.trainNumber || ev.train_number} - ${payload?.lineName || 'Ligne'}`;
                  } else if (ev.event_type === 'DOC_READ') {
                    badgeColor = "bg-cyan-500/10 text-cyan-400 border border-cyan-500/20";
                    eventTitle = "Lecture Doc";
                    details = `Consultation du document : ${payload?.file || 'Inconnu'}`;
                  } else if (ev.event_type === 'INCIDENT_REPORT') {
                    badgeColor = "bg-red-500/10 text-red-400 border border-red-500/20";
                    eventTitle = "Signalement Incident";
                    details = `Incident ${payload?.type?.toUpperCase() || 'Danger'} signalé au PK ${payload?.pk?.toFixed(3) || ev.pk} (${payload?.description || 'sans détails'})`;
                  } else if (ev.event_type === 'INCIDENT_RESOLVED') {
                    badgeColor = "bg-blue-500/10 text-blue-400 border border-blue-500/20";
                    eventTitle = "Résolution Incident";
                    details = `Incident ${payload?.incident?.type?.toUpperCase()} au PK ${payload?.incident?.pk?.toFixed(3)} levé comme ${payload?.status === 'ABSENT' ? 'ABSENT' : 'PRÉSENT'}`;
                  }

                  return (
                    <div key={ev.id} className="bg-slate-900/60 border border-white/5 p-4 rounded-xl flex flex-col sm:flex-row sm:items-center justify-between gap-3 hover:border-white/10 transition-colors">
                      <div className="space-y-1">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className={`px-2 py-0.5 text-[8px] font-black uppercase tracking-widest rounded ${badgeColor}`}>
                            {eventTitle}
                          </span>
                          <span className="text-[10px] font-mono text-cyan-400 font-bold">
                            TR {ev.train_number || 'SANS TRAIN'}
                          </span>
                          {ev.pk !== null && (
                            <span className="text-[10px] font-mono text-slate-500">
                              PK {parseFloat(ev.pk).toFixed(3)}
                            </span>
                          )}
                        </div>
                        <p className="text-white text-xs font-medium">{details}</p>
                        {ev.coords && (
                          <p className="text-[9px] text-slate-500 font-mono">Coordonnées: {ev.coords}</p>
                        )}
                      </div>
                      <div className="text-right shrink-0">
                        <p className="text-[9px] text-slate-500 uppercase tracking-widest font-black">Date & Heure</p>
                        <p className="text-[11px] font-mono text-white mt-0.5">
                          {new Date(ev.device_timestamp || ev.created_at).toLocaleString('fr-FR')}
                        </p>
                      </div>
                    </div>
                  );
                })
              )}
            </div>

            <div className="mt-6 flex justify-end">
              <button 
                onClick={() => setViewingActivityUser(null)} 
                className="py-3 px-6 bg-slate-800 hover:bg-slate-700 text-white text-xs font-black uppercase tracking-widest rounded-xl transition-colors"
              >
                Fermer
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
